import axios from 'axios'

export const aiBaseUrl = (() => {
  switch (process.env.VUE_APP_REAL_ENV) {
    case 'production': return 'https://mixedspace.njyjxr.com/api'
    case 'pre': return 'http://192.168.1.52/api'
    case 'test': return 'http://192.168.1.170/api'
    default: return 'http://192.168.1.170/api'
  }
})()

const aiRequest = axios.create({
  baseURL: aiBaseUrl,
  withCredentials: true,
})

aiRequest.interceptors.response.use((response) => response.data)

export interface AiCustomerFaq { question: string; answer: string }
export interface AiCustomerConversation { conversationId: string }
export interface AiCustomerChatFile { id: string; url: string }
export interface AiCustomerChatRecord {
  id: string | number; question: string; answer: string; files?: AiCustomerChatFile[]; followup?: { questions: string[] }; askedAt?: number; createdAt?: number
}
export interface AiCustomerChatHistoryItem { role: 'user' | 'assistant'; content: string }
export interface AiCustomerChatStreamEvent {
  type: 'classify' | 'retrieval' | 'think' | 'answer' | 'correction' | 'warning' | 'done' | 'followup'
  content?: string; questions?: string[]
}

export const getAiCustomerFaqs = () => aiRequest.get('/v1/ai/customer/faqs')
export const getAiCustomerChatRecords = (conversationId: string, params?: { limit?: number }) =>
  aiRequest.get(`/v1/ai/customer/conversations/${encodeURIComponent(conversationId)}/chat/records`, { params })
export const recordAiCustomerFaq = (conversationId: string, data: { platform: number; question: string; answer: string }) =>
  aiRequest.post(`/v1/ai/customer/conversations/${encodeURIComponent(conversationId)}/chat/faq`, data)
export const stopAiCustomerChat = (conversationId: string) =>
  aiRequest.post(`/v1/ai/customer/conversations/${encodeURIComponent(conversationId)}/chat/stop`)
export const uploadAiCustomerFile = (file: File) => {
  const data = new FormData()
  data.append('file', file)
  return aiRequest.post('/v1/ai/customer/files/upload', data, { headers: { 'Content-Type': 'multipart/form-data' } })
}

const eventTypes = new Set<AiCustomerChatStreamEvent['type']>(['classify', 'retrieval', 'think', 'answer', 'correction', 'warning', 'done', 'followup'])
const parseEvent = (payload: string): AiCustomerChatStreamEvent | undefined => {
  const value = payload.trim()
  if (!value) return undefined
  if (value === '[DONE]') return { type: 'done' }
  try {
    const data = JSON.parse(value) as Record<string, unknown>
    if (data.type === 'ping') return undefined
    if (typeof data.type !== 'string' || !eventTypes.has(data.type as AiCustomerChatStreamEvent['type'])) return undefined
    return {
      type: data.type as AiCustomerChatStreamEvent['type'],
      content: typeof data.content === 'string' ? data.content : undefined,
      questions: Array.isArray(data.questions) ? data.questions.filter((item): item is string => typeof item === 'string') : undefined,
    }
  } catch { return { type: 'answer', content: value } }
}

export const streamAiCustomerChat = async (
  conversationId: string,
  data: { platform: number; query: string; fileIds?: string[]; history?: AiCustomerChatHistoryItem[] },
  onEvent: (event: AiCustomerChatStreamEvent) => void,
  signal?: AbortSignal,
) => {
  const baseUrl = aiBaseUrl
  const response = await fetch(`${baseUrl}/v1/ai/customer/conversations/${encodeURIComponent(conversationId)}/chat/stream`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include', signal,
  })
  if (!response.ok) throw new Error(`AI customer service request failed: ${response.status}`)
  if (!response.body) {
    const event = parseEvent(await response.text())
    if (!event) throw new Error('AI customer service stream returned no events')
    onEvent(event)
    return
  }
  const reader = response.body.getReader()
  let eventCount = 0
  const decoder = new TextDecoder()
  let buffer = ''
  const consume = (packet: string) => {
    const dataLines = packet.split(/\r?\n/).filter((line) => line.startsWith('data:'))
    if (dataLines.length === 0) return
    const data = dataLines.map((line) => line.slice(5).trimStart()).join('\n')
    const event = parseEvent(data)
    if (event) { eventCount += 1; onEvent(event) }
  }
  try {
    let streamDone = false
    while (!streamDone) {
      const { done, value } = await reader.read()
      streamDone = done
      if (streamDone) break
      buffer += decoder.decode(value, { stream: true })
      const packets = buffer.split(/\r?\n\r?\n/)
      buffer = packets.pop() ?? ''
      packets.forEach(consume)
    }
    buffer += decoder.decode()
    consume(buffer)
    if (eventCount === 0) throw new Error('AI customer service stream returned no events')
  } finally { reader.releaseLock() }
}
